/**
 *  @swagger
 *  definitions:
 *    RegionSeasonRegattaLocationParams:
 *      type: object
 *      properties:
 *        RegionSeasonId:
 *          description: ID of the associated RegionSeason
 *          type: string
 *        Location:
 *          description: Location of the RegionSeasonRegattaLocation
 *          type: string
 *        GeoLocationLat:
 *          description: GeoLocationLat of the RegionSeasonRegattaLocation
 *          type: integer
 *        GeoLocationLng:
 *          description: GeoLocationLng of the RegionSeasonRegattaLocation
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    RegionSeasonRegattaLocationCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegionSeasonRegattaLocationParams'
 *        - type: object
 *          required:
 *            - RegionSeasonId
 *            - Location
 *
 *  @swagger
 *  definitions:
 *    RegionSeasonRegattaLocationQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegionSeasonRegattaLocation
 *              type: string
 *        - $ref: '#/definitions/RegionSeasonRegattaLocationParams'
 *
 *  @swagger
 *  definitions:
 *    RegionSeasonRegattaLocationUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegionSeasonRegattaLocationQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegionSeasonRegattaLocation:
 *      allOf:
 *        - $ref: '#/definitions/RegionSeasonRegattaLocationCreateParams'
 *        - $ref: '#/definitions/RegionSeasonRegattaLocationUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegionSeasonRegattaLocation:
 *        allOf:
 *          - $ref: '#/definitions/RegionSeasonRegattaLocation'
 *          - type: object
 *            properties:
 *              RegionSeason:
 *                description: RegionSeason of the RegionSeasonRegattaLocation
 *                $ref: '#/components/schemas/RegionSeason'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegionSeasonId: string;
};

export type $Params = {
  GeoLocationLat: null | number;
  GeoLocationLng: null | number;
  Location: string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegionSeason?: $Model<'RegionSeason'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  GeoLocationLat: {
    allowNull: true,
    type: Sequelize.DOUBLE(
      10,
      6,
    ),
  },
  GeoLocationLng: {
    allowNull: true,
    type: Sequelize.DOUBLE(
      10,
      6,
    ),
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Location: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegionSeasonRegattaLocation'> => {
  const RegionSeasonRegattaLocation = stack.define(
    'RegionSeasonRegattaLocation',
    fields(Sequelize),
    {
      tableName: 'RegionSeasonRegattaLocations',
    },
  );

  RegionSeasonRegattaLocation.associate = (models: $ModelClasses) => {
    models.RegionSeasonRegattaLocation.belongsTo(
      models.RegionSeason,
      {
        as: 'RegionSeason',
        foreignKey: 'RegionSeasonId',
      },
    );
  };

  RegionSeasonRegattaLocation.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegionSeasonRegattaLocation;
});
